/*
  APO URL Parameter Preselection
  ==============================

  Reads URL search params and preselects matching APO (Manifold) product options.

  URL format:
    ?option-label=value-handle
  Example:
    ?hand=left&steel-shafts=kbs-tour-regular&shaft-length=standard&grip-size=midsize

  How it works:
    1. On script load, checks for URL params. If none, does nothing.
    2. Listens for "apo:event" with detail.event === "init::finish".
    3. Builds a lookup map from mwApoInit.response, keyed by handleized option
       titles. Each value is keyed by its handleized text. Signed values get a
       second alias prefixed with "minus_" so the negative variant can be
       addressed when its bare handle collides with the positive (e.g. +1.5"
       and -1.5" both handleize to "1-5"; positive wins the bare key,
       negative is exposed as "minus_1-5").
    4. handleize: strip currency parentheticals like "(+$25.00 USD)" first,
       then NFD-normalize, drop quotes/brackets/parens and combining marks,
       collapse runs of non-word chars to "-", trim, lowercase.
    5. For each URL param matching an option, selects the value:
       - Radio/Checkbox: input.click()
       - Select: set .value and dispatch change

  Logging:
    ApoParamSelector.debug = true → enables [param-selector] console logs
*/

const ApoParamSelector = (() => {
  let debug = false;
  const LOG = '[param-selector]';
  const log = (...args) => { if (debug) console.log(LOG, ...args); };

  const applied = new Set();

  const stripPrice = (str) =>
    str.replace(/\([^)]*(?:[\$€£¥]|USD|EUR|GBP|CAD|AUD|NZD|JPY|CHF|SEK|NOK|DKK)[^)]*\)/gi, '');

  const handlelize = (str) =>
    stripPrice(String(str))
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[\[\]'()"]+/g, '')
      .replace(/([^\w]+|\s+)/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^-+|-+$/g, '')
      .toLowerCase();

  const buildResponseMap = () => {
    const map = {};
    if (typeof mwApoInit === 'undefined' || !mwApoInit.response) return map;

    const variantData = Object.values(mwApoInit.response)[0];
    if (!variantData || !variantData.options) return map;

    for (const [optionId, option] of Object.entries(variantData.options)) {
      const optionKey = option.title ? handlelize(option.title) : '';
      if (!optionKey) continue;

      const entry = { optionId, values: {} };

      if (variantData.values) {
        for (const [valueId, valueData] of Object.entries(variantData.values)) {
          if (valueData.option_id !== optionId) continue;
          const raw = valueData.value?.title || '';
          const bare = handlelize(raw);
          if (!bare) continue;

          const firstChar = raw.trim().charAt(0);
          if (firstChar === '-') {
            entry.values[`minus_${bare}`] = valueId;
            if (!(bare in entry.values)) entry.values[bare] = valueId;
          } else {
            if (!(bare in entry.values)) entry.values[bare] = valueId;
          }
        }
      }

      map[optionKey] = entry;
    }
    return map;
  };

  const buildQueue = () => {
    const params = new URLSearchParams(window.location.search);
    const responseMap = buildResponseMap();
    log('response map:', Object.keys(responseMap));

    const items = [];
    for (const [paramKey, paramValue] of params) {
      const key = handlelize(paramKey);
      const value = handlelize(paramValue);
      const optionEntry = responseMap[key];
      if (!optionEntry) continue;

      const valueId = optionEntry.values[value] || optionEntry.values[`minus_${value}`];
      if (!valueId) {
        log(`no match for "${key}"="${value}", available:`, Object.keys(optionEntry.values));
        continue;
      }
      items.push({ key, value, valueId, optionEntry });
    }
    return items;
  };

  const isCorrectValue = (optionEntry, valueId) => {
    const optionEl = document.getElementById(optionEntry.optionId);
    if (!optionEl) return false;

    const selectedPill = optionEl.querySelector('.mw-option__value--selected');
    if (selectedPill && selectedPill.getAttribute('data-value-id') === valueId) return true;

    const select = optionEl.querySelector('select.mw-option__control--type-select');
    if (select && select.value === valueId) return true;

    return false;
  };

  const applyOne = (item) => {
    const { key, valueId, optionEntry } = item;

    const optionEl = document.getElementById(optionEntry.optionId);
    if (!optionEl) {
      log(`"${key}": option element #${optionEntry.optionId} not in DOM`);
      return;
    }

    if (isCorrectValue(optionEntry, valueId)) return;

    const input = document.getElementById(valueId);
    if (input && (input.type === 'radio' || input.type === 'checkbox')) {
      log(`"${key}" → clicking ${input.type} #${valueId}`);
      input.click();
      return;
    }

    const select = optionEl.querySelector('select.mw-option__control--type-select');
    if (select) {
      log(`"${key}" → setting select to ${valueId}`);
      select.value = valueId;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const processQueue = (queue) => {
    for (const item of queue) {
      applyOne(item);
      applied.add(item.key);
    }
    log('applied:', [...applied]);
  };

  const init = () => {
    const params = new URLSearchParams(window.location.search);
    if (!params.size) return;

    log('params:', Object.fromEntries(params));
    log('waiting for init::finish');

    window.addEventListener('apo:event', function onApoEvent(e) {
      if (e.detail.event !== 'init::finish') return;
      window.removeEventListener('apo:event', onApoEvent);

      log('init::finish received');
      const queue = buildQueue();
      log('queue:', queue.map(i => `${i.key}=${i.value}`));
      processQueue(queue);
    });
  };

  init();

  return {
    get debug() { return debug; },
    set debug(v) { debug = v; },
    handlelize,
    applied,
  };
})();
